import styled, { css } from 'styled-components'

import { Image, Text } from '@sketch/components'

import {
  CARD_RADIUS,
  CARD_SHADOW,
  DASHED_SEPARATOR,
  HAIRLINE_SHADOW,
  TILE_MEDIA_SIZE,
} from './constants'
import { breakpoint } from '@sketch/global-styles'

/**
 * Two-tile preview card shared by the downloadable-asset and add-share-member
 * previews: a centred row of tiles split by a dashed `Separator`, with the
 * notification's primary action (Accept / Download) centred below them.
 */
export const Card = styled.section(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 40px;
    width: 100%;
    padding: 40px 20px 20px;
    border-radius: ${CARD_RADIUS};
    background: ${theme.colors.background.secondary.A};

    box-shadow: ${CARD_SHADOW};

    ${breakpoint('md')`
        padding: 64px 40px 40px;
    `}
  `
)

export const TileWrapper = styled.div`
  display: flex;
  justify-content: space-evenly;
  width: 100%;
`

export const Tile = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  min-width: 0;
  text-align: center;
  flex: 1;
`

export const Separator = styled.div`
  border-left: 1px dashed ${DASHED_SEPARATOR};
`

export const Thumbnail = styled.div(
  ({ theme }) => css`
    width: ${TILE_MEDIA_SIZE};
    height: ${TILE_MEDIA_SIZE};
    border-radius: 12px;
    margin-bottom: 10px;
    background: ${theme.colors.foreground.secondary.A};
  `
)

/**
 * Square tile holding a single icon (e.g. the download or star glyph) in place
 * of a document thumbnail. The icon itself is passed as children and sized by
 * the caller.
 */
export const IconTile = styled.div(
  ({ theme }) => css`
    width: ${TILE_MEDIA_SIZE};
    height: ${TILE_MEDIA_SIZE};
    border-radius: 12px;
    margin-bottom: 10px;

    display: inline-flex;
    align-items: center;
    justify-content: center;

    background: ${theme.colors.background.tertiary.C};
    color: ${theme.colors.foreground.secondary.C};
    box-shadow: ${HAIRLINE_SHADOW};
  `
)

/** Document thumbnail variant of `Thumbnail`, used when a preview image exists. */
export const TileThumbnail = styled(Image).attrs({ objectFit: 'cover' })(
  ({ theme }) => css`
    display: block;
    width: ${TILE_MEDIA_SIZE};
    height: ${TILE_MEDIA_SIZE};
    border-radius: 12px;
    margin-bottom: 10px;
    overflow: hidden;
    background: ${theme.colors.background.canvas.A};
  `
)

export const TileTitle = styled(Text).attrs({
  textStyle: 'prominent.secondary.D',
})`
  margin: 0;
  max-width: 160px;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: ${({ theme }) => theme.fontWeights.regular};
`

export const TileMeta = styled(Text).attrs({
  textStyle: 'prominent.secondary.D',
})`
  margin: 0;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.foreground.secondary.D};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
`
